import React, { useEffect } from 'react';
import { ListWidget } from '../widget/list';
import { IMemoryListItem, ThingTypes, transformForList } from '../../model/ryve/Thing';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { fetchNextPageSearchEntriesRequestAction } from '../../data/actions/search';
import { ISearchResult } from '../../model/search/SearchResult';
import { FramedContent } from '../framed-content';
import { INotification } from '../../model/notifications';
import { userprofileMemorylistRequestAction } from '../../data/actions/foundation';
import { setFlowDataAction } from '../../data/actions/flow';
import { Typo, useBreakpoints } from '@curry-group/mui-curcuma';
import { ISearchRequestBody } from '../../model/search/SearchRequest';
import { useFilterState } from '../../data/sagas/filter';
import { ConnectedBotSearchResults } from '../bot-search-results';

export interface ISearchResultProps {
  alias: string;
  result?: ISearchResult | null;
  error?: string;
  first?: boolean;
  working?: boolean;
  firstPageWorking?: boolean;
  moreDataAvailable?: boolean;
  unreadNotifications?: Array<INotification>;
  fetchNextPageSearchEntries?: () => void;
  memoryIds?: Array<string>;
  itemMemoryClicked?: (item: IMemoryListItem, type: 'GET'|'POST'|'DELETE') => void;
  profileId?: string;
  shareClick?: (e:any) => void;
  searchObject?: ISearchRequestBody;
  isMobile?: boolean;
}

export const SearchResults: React.FC<ISearchResultProps> = ({
  alias,
  result,
  error,
  first,
  working,
  firstPageWorking,
  moreDataAvailable,
  unreadNotifications,
  fetchNextPageSearchEntries,
  memoryIds,
  itemMemoryClicked,
  profileId,
  shareClick
}) => {
  const botSearchActive = useSelector(state => state.bot?.botSearchActive);
  // A fresh (first-page) search in progress: hide any stale results and show only skeletons.
  // Gating on `working` means a completed search (working=false) immediately reveals results.
  const replaceWithSkeletons = !!(working && firstPageWorking) && !botSearchActive;

  const handleScroll = () => {
    if (window.innerHeight + document.documentElement.scrollTop + window.innerHeight * 1.2 <= document.documentElement.offsetHeight) return;
    if (!working && moreDataAvailable) fetchNextPageSearchEntries?.();
  }
  
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  return (
    <FramedContent pt={botSearchActive ? 2 : undefined}>
      {botSearchActive && <ConnectedBotSearchResults alias={alias} />}
      <ListWidget
        replaceWithSkeletons={replaceWithSkeletons}
        working={(first && !botSearchActive) || working}
        items={transformForList(result?.hits?.hits.map(hit => {
          return {
            ...hit,
            badge: !!unreadNotifications?.find(notification => notification.content?.itemRef === hit._id),
            booked: hit._source?.type === ThingTypes.News
              ? (!!hit._source?.content.url ? memoryIds?.includes(hit._source?.content.url) : false)
              : memoryIds?.includes(hit._id),
            noShare: hit._source?.type === ThingTypes.OneToOne || ((hit._source?.type === ThingTypes.Group || hit._source?.type === ThingTypes.Project) && !hit._source?.content?.listed),
            profileId: memoryIds || hit._source?.type === ThingTypes.OneToOne ? profileId : undefined
          };
        }))}
        noWorkingHeadline={botSearchActive}
        headline={botSearchActive
          ? alias === 'experts-actors' ? 'Neuste Mitglieder' : 'Aktuelle Beiträge'
          : result?.hits?.total
            ? `Suchergebnisse (${result.hits.total})`
            : 'Keine Suchergebnisse'}
        itemMemoClicked={itemMemoryClicked}
        shareClick={shareClick}
      />
    </FramedContent>
  );
};

export const ConnectedSearchContent: React.FC<{alias: string}> = ({alias}) => {
  const history = useHistory();
  const isMobile = !!useBreakpoints().mdDown;
  const result = useSelector(state => state.search?.result);
  const error = useSelector(state => state.search?.error);
  const first = useSelector(state => state.search?.first);
  const working = useSelector(state => state.search?.working);
  const firstPageWorking = useSelector(state => state.search?.firstPageWorking);
  const moreDataAvailable = useSelector(state => state.search?.moreDataAvailable);
  const notifications = useSelector(state => state.notifications?.items);
  const memoryItems = useSelector(state => state.foundation?.profile?.memoryList);
  const profileId = useSelector(state => state.foundation?.profile?.profileId);
  const { searchObject } = useFilterState();
  
  const dispatch = useDispatch();
  const dispatcher = {
    fetchNextPageSearchEntriesAction: () => dispatch(fetchNextPageSearchEntriesRequestAction?.({})),
    itemMemoryClicked: (item: IMemoryListItem, type: 'GET'|'POST'|'DELETE') => dispatch(userprofileMemorylistRequestAction({
      memoryItem: item,
      type: type
    })),
    shareClick: (detailItem) => {
      dispatch(setFlowDataAction({
        alias: 'share',
        flowData: {
          location: history.location,
          itemData: { sharedItem: detailItem }
        }
      }));
      history.push(history.location.pathname + '/flow' + history.location.search);
    }
  };

  const memoryIds = memoryItems?.map(m => m.typeId === ThingTypes.News ? (m.externalId ?? '') : m.elementId);

  return (
    <SearchResults
      alias={alias}
      result={result}
      error={error}
      first={first}
      working={working}
      firstPageWorking={firstPageWorking}
      moreDataAvailable={moreDataAvailable}
      unreadNotifications={notifications.filter(notification => !notification.content?.notificationRead)}
      fetchNextPageSearchEntries={dispatcher.fetchNextPageSearchEntriesAction}
      memoryIds={memoryIds}
      itemMemoryClicked={dispatcher.itemMemoryClicked}
      profileId={profileId}
      shareClick={dispatcher.shareClick}
      searchObject={searchObject}
      isMobile={isMobile}
    />
  );
};
